<!-- app-subdomain-creators/src/Components/CreatorChatFilePreview.vue -->
<template>
  <a
    :href="url"
    target="_blank"
    rel="noopener noreferrer"
    class="block w-full 2xl:w-[500px] rounded-xl border border-zinc-200 bg-zinc-50 mt-2 shadow-sm hover:bg-zinc-100 transition"
    @click.prevent="handleClick"
  >
    <!-- flex row so the left column can be full-height -->
    <div class="flex items-stretch">
      <!-- LEFT: icon stripe -->
      <div
        class="flex items-center justify-center w-11 bg-zinc-100 border-r border-zinc-200 rounded-l-xl"
      >
        <DocumentIcon class="w-5 h-5 text-zinc-500" />
      </div>

      <!-- RIGHT: avatar + title + time -->
      <div class="flex items-center justify-between flex-1 min-w-0 px-3 py-4">
        <!-- avatar + text -->
        <div class="flex items-center items gap-3 min-w-0">
          <!-- sender avatar -->
          <div
            class="flex items-center justify-center w-8 h-8 rounded-full bg-zinc-200 overflow-hidden shrink-0"
          >
            <img
              v-if="avatarSrc"
              :src="avatarSrc"
              alt=""
              class="w-full h-full object-cover"
            />
          </div>

          <!-- text block -->
          <div class="flex min-w-0">
            <!-- main label (file name / title) -->
            <div class="relative text-xs font-semibold text-zinc-800 truncate max-w-[180px] sm:max-w-[240px]">
              {{ title || displayHostname }}
            </div>

            <!-- file meta: type + size -->
            <div
              v-if="fileMetaLabel"
              class="absolute mt-4 text-xs text-zinc-500 truncate max-w-[180px] sm:max-w-[240px]"
            >
              {{ fileMetaLabel }}
            </div>
          </div>
        </div>

        <!-- date/time -->
        <div
          v-if="createdAtLabel"
          class="ml-3 text-xs text-zinc-400 whitespace-nowrap"
        >
          {{ createdAtLabel }}
        </div>
      </div>
    </div>
  </a>
</template>

<script setup>
import { computed, defineProps } from 'vue';
import { DocumentIcon } from '@heroicons/vue/24/outline';

const defaultAvatarSrc = require('@/assets/media/images/default-avatar.png');

const props = defineProps({
  url: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    default: '',
  },
  createdAt: {
    type: [String, Number, Date],
    default: null,
  },
  // 'creator' | 'member' | null
  senderType: {
    type: String,
    default: null,
  },
  creatorAvatarSrc: {
    type: String,
    default: '',
  },
  memberAvatarSrc: {
    type: String,
    default: '',
  },
  // optional: MIME type, e.g. "application/pdf"
  mime: {
    type: String,
    default: '',
  },
  // optional: size in bytes
  sizeBytes: {
    type: [Number, String],
    default: null,
  },
});

// 🔹 Emit up so the parent (main component) can decide how to open
const emit = defineEmits(['open-file']);

const displayHostname = computed(() => {
  try {
    const u = new URL(props.url);
    return u.hostname.replace(/^www\./, '');
  } catch (e) {
    return props.url;
  }
});

const createdAtLabel = computed(() => {
  if (!props.createdAt) return '';
  const d = new Date(props.createdAt);
  if (Number.isNaN(d.getTime())) return '';

  // 06/26 7:28 PM style
  return d.toLocaleString(undefined, {
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
});

const avatarSrc = computed(() => {
  // prefer real sender if we have it
  if (props.senderType === 'member') {
    return props.memberAvatarSrc || props.creatorAvatarSrc || defaultAvatarSrc;
  }
  if (props.senderType === 'creator') {
    return props.creatorAvatarSrc || props.memberAvatarSrc || defaultAvatarSrc;
  }

  // fallback: show the other participant first
  return props.memberAvatarSrc || props.creatorAvatarSrc || defaultAvatarSrc;
});

// Derive a short file type label from mime or extension
const fileTypeLabel = computed(() => {
  if (props.mime) {
    const lower = String(props.mime).toLowerCase().trim();

    // Special-case common types
    if (lower === 'text/plain') return 'TXT';

    // e.g. "application/pdf" -> "PDF"
    const parts = lower.split('/');
    const tail = parts[parts.length - 1];
    if (tail) {
      return tail.replace(/[-_]/g, ' ').toUpperCase();
    }
  }

  // Fallback: infer from title or URL extension
  const source = props.title || props.url;
  const match = source.match(/\.([a-zA-Z0-9]+)(\?|#|$)/);
  if (match && match[1]) {
    return match[1].toUpperCase();
  }

  return '';
});


// Human-readable size, e.g. "1.2 MB"
const sizeLabel = computed(() => {
  if (props.sizeBytes === null || props.sizeBytes === undefined || props.sizeBytes === '') {
    return '';
  }

  const bytes = Number(props.sizeBytes);
  if (!Number.isFinite(bytes) || bytes <= 0) return '';

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let idx = 0;
  let value = bytes;

  while (value >= 1024 && idx < units.length - 1) {
    value /= 1024;
    idx += 1;
  }

  const rounded = value >= 10 ? value.toFixed(0) : value.toFixed(1);
  return `${rounded} ${units[idx]}`;
});

// Combined meta label
const fileMetaLabel = computed(() => {
  if (fileTypeLabel.value && sizeLabel.value) {
    return `${fileTypeLabel.value} · ${sizeLabel.value}`;
  }
  if (fileTypeLabel.value) return fileTypeLabel.value;
  if (sizeLabel.value) return sizeLabel.value;
  return '';
});

/**
 * Decide how we want the overlay to treat this file.
 * 'pdf'  -> open PDF overlay
 * 'text' -> open text overlay
 * 'image'-> open image overlay
 * null   -> fallback to direct new tab
 */
const overlayKind = computed(() => {
  const urlLower = (props.url || '').toLowerCase();
  const mimeLower = (props.mime || '').toLowerCase();

  if (mimeLower === 'application/pdf' || urlLower.endsWith('.pdf')) {
    return 'pdf';
  }
  if (mimeLower === 'text/plain' || urlLower.endsWith('.txt')) {
    return 'text';
  }
  if (mimeLower.startsWith('image/')) {
    return 'image';
  }

  return null; // unknown → let browser handle it
});

const handleClick = (event) => {
  event?.preventDefault?.();

  const kind = overlayKind.value;
  const name = props.title || displayHostname.value;

  // if we know how to handle it in the overlay, emit to parent
  if (kind) {
    emit('open-file', {
      src: props.url,
      type: kind,  // 'pdf' | 'text' | 'image'
      name,
    });
    return;
  }

  // fallback: behave like a normal link
  window.open(props.url, '_blank', 'noopener,noreferrer');
};
</script>

